import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "errorPlan",
    "errorPlanAmountTeamLabel",
    "errorPlanNoticesTeamLabel",
    "errorPlanPurgeDaysTeamLabel",
    "errorPlanAmountBusinessLabel",
    "errorPlanNoticesBusinessLabel",
    "errorPlanPurgeDaysBusinessLabel",
    "insightsPlan",
    "insightsPlanAmountTeamLabel",
    "insightsPlanQuotaTeamLabel",
    "insightsPlanPurgeDaysTeamLabel",
    "insightsPlanAmountBusinessLabel",
    "insightsPlanQuotaBusinessLabel",
    "insightsPlanPurgeDaysBusinessLabel",
    "monitoringPlanAmountTeamLabel",
    "monitoringPlanAmountBusinessLabel",
    "monitoringPlanDetailTeamLabel",
    "monitoringPlanDetailBusinessLabel",
    "statusPagesPlanAmountTeamLabel",
    "statusPagesPlanAmountBusinessLabel",
    "statusPagesPlanDetailTeamLabel",
    "statusPagesPlanDetailBusinessLabel",
    "basePlanAmountTeamLabel",
    "basePlanAmountBusinessLabel",
    "totalAmountTeamLabel",
    "totalAmountBusinessLabel",
    "originalTotalAmountTeamLabel",
    "originalTotalAmountBusinessLabel",
    "originalTotalAmountTeam",
    "originalTotalAmountBusiness",
    "discountNote",
    "discountLabel",
    "teamSubscriptionPlanId",
    "businessSubscriptionPlanId",
    "teamErrorSubscriptionPlanId",
    "businessErrorSubscriptionPlanId",
    "teamInsightsSubscriptionPlanId",
    "businessInsightsSubscriptionPlanId",
    "teamMonitoringSubscriptionPlanId",
    "businessMonitoringSubscriptionPlanId",
    "teamStatusPagesSubscriptionPlanId",
    "businessStatusPagesSubscriptionPlanId",
    "teamPlanSubmit",
    "currentTeamPlanMessage",
    "businessPlanSubmit",
    "currentBusinessPlanMessage",
    "monthButton",
    "yearButton",
    "intervalLabel",
  ];

  static values = {
    currentPlanIds: Array,
    includedMonitorCount: Number,
    includedStatusPagesCount: Number,
    monitorCount: Number,
    statusPagesCount: Number,
    basePlanData: Object,
    monitoringPlanData: Object,
    statusPagesPlanData: Object,
    discountPercentOff: Number,
    discountAmountOff: Number,
  };

  connect() {
    this.selectedInterval = "month";
    this.baseTeamAmount = 0;
    this.baseBusinessAmount = 0;
    this.monitoringTeamAmount = 0;
    this.monitoringBusinessAmount = 0;
    this.statusPagesTeamAmount = 0;
    this.statusPagesBusinessAmount = 0;
    this.errorTeamAmount = 0;
    this.errorBusinessAmount = 0;
    this.insightsTeamAmount = 0;
    this.insightsBusinessAmount = 0;

    this.updatePricing();
  }

  // The discount lives on the Stripe customer, which this page does not read
  // when it renders. The lazily loaded subscription-resources frame does, and
  // publishes it on an element for us to fold into the totals when it lands.
  discountLoaded(event) {
    const source = event.target.querySelector("[data-subscription-discount]");
    if (!source) return;

    this.discountPercentOffValue = parseFloat(source.dataset.percentOff) || 0;
    this.discountAmountOffValue = parseFloat(source.dataset.amountOff) || 0;
    this.discountLabelTargets.forEach(
      (target) => (target.textContent = source.dataset.label || ""),
    );
    this.updatePricing();
  }

  updatePricing() {
    this.intervalUpdate();
    this.basePlanUpdate();
    this.errorPlanUpdate();
    this.insightsPlanUpdate();
    this.monitoringPlanUpdate();
    this.statusPagesPlanUpdate();
    this.totalAmountUpdate();
  }

  intervalUpdate() {
    if (this.monthButtonTarget.checked) {
      this.selectedInterval = "month";
    } else {
      this.selectedInterval = "year";
    }
    if (this.hasIntervalLabelTarget) {
      this.intervalLabelTargets.forEach(
        (target) => (target.innerHTML = this.selectedInterval),
      );
    }
  }

  basePlanUpdate() {
    this.baseTeamPlan = this.basePlanDataValue["team"][this.selectedInterval];
    this.baseBusinessPlan =
      this.basePlanDataValue["business"][this.selectedInterval];

    this.baseTeamAmount = parseFloat(this.baseTeamPlan.amount);
    this.baseBusinessAmount = parseFloat(this.baseBusinessPlan.amount);

    this.basePlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.baseTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.basePlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.baseBusinessAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );

    this.teamSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.baseTeamPlan.id),
    );
    this.businessSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.baseBusinessPlan.id),
    );
  }

  monitoringPlanUpdate() {
    this.monitoringTeamPlan =
      this.monitoringPlanDataValue["team"][this.selectedInterval];
    this.monitoringBusinessPlan =
      this.monitoringPlanDataValue["business"][this.selectedInterval];

    this.monitoringTeamAmount =
      parseFloat(this.monitoringTeamPlan.amount) * this.extraMonitorCount();
    this.monitoringBusinessAmount =
      parseFloat(this.monitoringBusinessPlan.amount) * this.extraMonitorCount();

    this.monitoringPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.monitoringTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.monitoringPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.monitoringBusinessAmount.toLocaleString(
          "en-US",
          { maximumFractionDigits: 0 },
        )),
    );

    this.monitoringPlanDetailTeamLabelTargets.forEach(
      (target) =>
        (target.textContent = this.usageDetail(
          this.includedMonitorCountValue,
          this.monitoringTeamPlan.amount,
        )),
    );
    this.monitoringPlanDetailBusinessLabelTargets.forEach(
      (target) =>
        (target.textContent = this.usageDetail(
          this.includedMonitorCountValue,
          this.monitoringBusinessPlan.amount,
        )),
    );

    this.teamMonitoringSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.monitoringTeamPlan.id),
    );
    this.businessMonitoringSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.monitoringBusinessPlan.id),
    );
  }

  statusPagesPlanUpdate() {
    this.statusPagesTeamPlan =
      this.statusPagesPlanDataValue["team"][this.selectedInterval];
    this.statusPagesBusinessPlan =
      this.statusPagesPlanDataValue["business"][this.selectedInterval];

    this.statusPagesTeamAmount =
      parseFloat(this.statusPagesTeamPlan.amount) * this.extraStatusPageCount();
    this.statusPagesBusinessAmount =
      parseFloat(this.statusPagesBusinessPlan.amount) *
      this.extraStatusPageCount();

    this.statusPagesPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.statusPagesTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.statusPagesPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.statusPagesBusinessAmount.toLocaleString(
          "en-US",
          { maximumFractionDigits: 0 },
        )),
    );

    this.statusPagesPlanDetailTeamLabelTargets.forEach(
      (target) =>
        (target.textContent = this.usageDetail(
          this.includedStatusPagesCountValue,
          this.statusPagesTeamPlan.amount,
        )),
    );
    this.statusPagesPlanDetailBusinessLabelTargets.forEach(
      (target) =>
        (target.textContent = this.usageDetail(
          this.includedStatusPagesCountValue,
          this.statusPagesBusinessPlan.amount,
        )),
    );

    this.teamStatusPagesSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.statusPagesTeamPlan.id),
    );
    this.businessStatusPagesSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.statusPagesBusinessPlan.id),
    );
  }

  errorPlanUpdate() {
    let selectedOption =
      this.errorPlanTarget.options[this.errorPlanTarget.selectedIndex];
    this.errorTeamPlan = JSON.parse(selectedOption.dataset.planData)["team"][
      this.selectedInterval
    ];
    this.errorBusinessPlan = JSON.parse(selectedOption.dataset.planData)[
      "business"
    ][this.selectedInterval];

    this.errorTeamAmount = parseFloat(this.errorTeamPlan.amount);
    this.errorBusinessAmount = parseFloat(this.errorBusinessPlan.amount);

    this.errorPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.errorTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.errorPlanNoticesTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.errorTeamAmount > 0 ? "" : " included"}`),
    );
    this.errorPlanPurgeDaysTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.errorTeamPlan.purge_days} days retention`),
    );

    this.errorPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.errorBusinessAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.errorPlanNoticesBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.errorBusinessAmount > 0 ? "" : " included"}`),
    );
    this.errorPlanPurgeDaysBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.errorBusinessPlan.purge_days} days retention`),
    );

    this.teamErrorSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.errorTeamPlan.id),
    );
    this.businessErrorSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.errorBusinessPlan.id),
    );
  }

  insightsPlanUpdate() {
    let selectedOption =
      this.insightsPlanTarget.options[this.insightsPlanTarget.selectedIndex];
    this.insightsTeamPlan = JSON.parse(selectedOption.dataset.planData)["team"][
      this.selectedInterval
    ];
    this.insightsBusinessPlan = JSON.parse(selectedOption.dataset.planData)[
      "business"
    ][this.selectedInterval];

    this.insightsTeamAmount = parseFloat(this.insightsTeamPlan.amount);
    this.insightsBusinessAmount = parseFloat(this.insightsBusinessPlan.amount);

    this.insightsPlanAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.insightsTeamAmount.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })),
    );
    this.insightsPlanQuotaTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.insightsTeamAmount > 0 ? "" : " included"}`),
    );
    this.insightsPlanPurgeDaysTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.insightsTeamPlan.purge_days} days retention`),
    );

    this.insightsPlanAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.insightsBusinessAmount.toLocaleString(
          "en-US",
          { maximumFractionDigits: 0 },
        )),
    );
    this.insightsPlanQuotaBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${selectedOption.text}${this.insightsBusinessAmount > 0 ? "" : " included"}`),
    );
    this.insightsPlanPurgeDaysBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = `${this.insightsBusinessPlan.purge_days} days retention`),
    );

    this.teamInsightsSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.insightsTeamPlan.id),
    );
    this.businessInsightsSubscriptionPlanIdTargets.forEach(
      (target) => (target.value = this.insightsBusinessPlan.id),
    );
  }

  totalAmountUpdate() {
    // Stripe discounts the invoice total rather than the individual line
    // items, so the subtotals above stay at list price and only these totals
    // reflect the account's coupon.
    this.subtotalTeamAmount =
      this.baseTeamAmount +
      this.errorTeamAmount +
      this.insightsTeamAmount +
      this.monitoringTeamAmount +
      this.statusPagesTeamAmount;
    this.subtotalBusinessAmount =
      this.baseBusinessAmount +
      this.errorBusinessAmount +
      this.insightsBusinessAmount +
      this.monitoringBusinessAmount +
      this.statusPagesBusinessAmount;

    this.totalTeamAmount = this.applyDiscount(this.subtotalTeamAmount);
    this.totalBusinessAmount = this.applyDiscount(this.subtotalBusinessAmount);

    this.totalAmountTeamLabelTargets.forEach(
      (target) => (target.innerHTML = this.formatAmount(this.totalTeamAmount)),
    );
    this.totalAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.formatAmount(this.totalBusinessAmount)),
    );
    this.originalTotalAmountTeamLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.formatAmount(this.subtotalTeamAmount)),
    );
    this.originalTotalAmountBusinessLabelTargets.forEach(
      (target) =>
        (target.innerHTML = this.formatAmount(this.subtotalBusinessAmount)),
    );

    const discounted = this.discounted();
    [
      ...this.originalTotalAmountTeamTargets,
      ...this.originalTotalAmountBusinessTargets,
      ...this.discountNoteTargets,
    ].forEach((target) => target.classList.toggle("hidden", !discounted));

    if (
      JSON.stringify(this.selectedTeamPlanIds().sort()) !==
      JSON.stringify(this.currentPlanIdsValue.sort())
    ) {
      this.teamPlanSubmitTarget.classList.remove("hidden");
      this.currentTeamPlanMessageTarget.classList.add("hidden");
    } else {
      this.teamPlanSubmitTarget.classList.add("hidden");
      this.currentTeamPlanMessageTarget.classList.remove("hidden");
    }

    if (
      JSON.stringify(this.selectedBusinessPlanIds().sort()) !==
      JSON.stringify(this.currentPlanIdsValue.sort())
    ) {
      this.businessPlanSubmitTarget.classList.remove("hidden");
      this.currentBusinessPlanMessageTarget.classList.add("hidden");
    } else {
      this.businessPlanSubmitTarget.classList.add("hidden");
      this.currentBusinessPlanMessageTarget.classList.remove("hidden");
    }
  }

  selectedTeamPlanIds() {
    return [
      this.baseTeamPlan.id,
      this.errorTeamPlan.id,
      this.insightsTeamPlan.id,
      this.monitoringTeamPlan.id,
      this.statusPagesTeamPlan.id,
    ];
  }

  selectedBusinessPlanIds() {
    return [
      this.baseBusinessPlan.id,
      this.errorBusinessPlan.id,
      this.insightsBusinessPlan.id,
      this.monitoringBusinessPlan.id,
      this.statusPagesBusinessPlan.id,
    ];
  }

  discounted() {
    return this.discountPercentOffValue > 0 || this.discountAmountOffValue > 0;
  }

  applyDiscount(amount) {
    if (this.discountPercentOffValue > 0) {
      return Math.max(
        this.roundAmount(amount * (1 - this.discountPercentOffValue / 100)),
        0,
      );
    }
    if (this.discountAmountOffValue > 0) {
      return Math.max(
        this.roundAmount(amount - this.discountAmountOffValue),
        0,
      );
    }
    return amount;
  }

  roundAmount(amount) {
    return Math.round(amount * 100) / 100;
  }

  // Plan prices are whole dollars, but a discount can leave cents behind.
  formatAmount(amount) {
    const rounded = this.roundAmount(amount);
    const fractionDigits = Number.isInteger(rounded) ? 0 : 2;
    return rounded.toLocaleString("en-US", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  extraMonitorCount() {
    return Math.max(this.monitorCountValue - this.includedMonitorCountValue, 0);
  }

  extraStatusPageCount() {
    return Math.max(
      this.statusPagesCountValue - this.includedStatusPagesCountValue,
      0,
    );
  }

  // States the pricing rule for a usage-billed line, e.g. "5 included, then
  // $1 each", so the amount next to it doesn't read as a plan limit.
  usageDetail(included, unitAmount) {
    const unit = this.formatAmount(parseFloat(unitAmount));
    return `${included} included, then $${unit} each`;
  }
}
